/**
 * Ads Service - admin-only routes under /api/ads.
 *
 * Unlike other domain services in this codebase, ads is admin-only - the
 * client portal never sees boost recommendations or campaigns. So no
 * context branching here; uses `apiClient` directly.
 */

import { apiClient } from "@/services/apiClient";
import type {
  AdAnalyticsSummary,
  BoostCandidatesView,
  BoostRecommendation,
  BoostRunResponse,
  BudgetView,
  BudgetUpdate,
  Campaign,
  CampaignStatus,
  CreateCampaignsResponse,
  ImportExternalCampaignsResponse,
  PostPerformanceResponse,
  StandaloneAdLatestResponse,
  StandaloneAdRequest,
  StandaloneAdResponse,
} from "../types";

function handleError(error: unknown, defaultMessage: string): never {
  console.error(`AdsService Error: ${defaultMessage}`, error);
  if (error instanceof Error) throw error;
  throw new Error(defaultMessage);
}

// Long-running endpoints. The Claude agent runs a multi-turn tool-use
// loop (5+ round trips to Anthropic + Meta) — comfortably longer than
// the apiClient's default 30s timeout. Campaign creation hits Meta's
// Marketing API per platform and can also stretch. 3 minutes is safe
// headroom without letting genuinely hung requests hang forever.
const AGENT_TIMEOUT_MS = 180_000;

interface RecommendBoostResponse extends BoostRunResponse {}

interface CampaignListResponse {
  campaigns: Campaign[];
}

export interface BoostRecommendationLatestResponse {
  task: {
    id: string;
    status: string;
    outputData: BoostRunResponse | null;
    inputData: Record<string, unknown> | null;
    errorMessage: string | null;
    createdAt: string | null;
    completedAt: string | null;
  } | null;
}

export const adsService = {
  /** Ask Claude to produce a boost recommendation for a specific post. */
  async recommendBoost(postId: string): Promise<RecommendBoostResponse> {
    try {
      return await apiClient.post<RecommendBoostResponse>(
        "/api/ads/recommend-boost",
        { postId },
        { timeout: AGENT_TIMEOUT_MS }
      );
    } catch (error) {
      handleError(error, "Failed to generate boost recommendation");
    }
  },

  /** Fetch the most recent DECIDE_BOOST task for a post, if any.
   *
   * Used by the boost modal on open to hydrate from the daily
   * pre-recommendation task without re-running Claude. Returns
   * `{task: null}` when nothing is cached; the modal then falls back
   * to recommendBoost(). */
  async getLatestBoostRecommendation(
    postId: string,
  ): Promise<BoostRecommendationLatestResponse> {
    try {
      return await apiClient.get<BoostRecommendationLatestResponse>(
        `/api/ads/boost-recommendation/latest?post_id=${encodeURIComponent(postId)}`,
      );
    } catch (error) {
      handleError(error, "Failed to load prior boost recommendation");
    }
  },

  /** Fetch the most recent standalone-ad task for a post, if any.
   *
   * Used by the modal on open to hydrate from the last agent
   * recommendation + submission params without re-running Claude.
   * Returns `{task: null}` when the post has never been submitted;
   * the modal runs the agent fresh in that case. */
  async getLatestStandaloneAd(postId: string): Promise<StandaloneAdLatestResponse> {
    try {
      return await apiClient.get<StandaloneAdLatestResponse>(
        `/api/ads/standalone-ad/latest?post_id=${encodeURIComponent(postId)}`,
      );
    } catch (error) {
      handleError(error, "Failed to load prior standalone-ad recommendation");
    }
  },

  /** Ask Claude to recommend budget + targeting for a standalone ad.
   *
   * Unlike recommendBoost, no platform eligibility probe happens — the
   * ad doesn't reference an existing organic post. Returned shape
   * matches BoostRunResponse so the UI can reuse the review pattern. */
  async recommendStandaloneAd(postId: string): Promise<BoostRunResponse> {
    try {
      return await apiClient.post<BoostRunResponse>(
        "/api/ads/recommend-standalone-ad",
        { postId },
        { timeout: AGENT_TIMEOUT_MS },
      );
    } catch (error) {
      handleError(error, "Failed to generate standalone-ad recommendation");
    }
  },

  /** Ask Claude to suggest boosts for already-published commercial posts. */
  async suggestBoosts(
    clientId: string,
    limit = 5
  ): Promise<BoostRunResponse> {
    try {
      return await apiClient.post<BoostRunResponse>(
        "/api/ads/suggest-boosts",
        { clientId, limit },
        { timeout: AGENT_TIMEOUT_MS }
      );
    } catch (error) {
      handleError(error, "Failed to generate boost suggestions");
    }
  },

  /** Approve a recommendation and create per-platform campaigns.
   *
   * Each enabled platform block becomes its own Meta campaign. Response
   * reports per-platform outcome; partial success is possible (e.g.
   * FB goes live, IG fails prereq). */
  async createCampaigns(
    postId: string,
    recommendation: BoostRecommendation
  ): Promise<CreateCampaignsResponse> {
    try {
      return await apiClient.post<CreateCampaignsResponse>(
        "/api/ads/create-campaign",
        { postId, recommendation },
        { timeout: AGENT_TIMEOUT_MS }
      );
    } catch (error) {
      handleError(error, "Failed to create campaign");
    }
  },

  async pauseCampaign(campaignId: string): Promise<Campaign> {
    try {
      return await apiClient.post<Campaign>(
        `/api/ads/campaigns/${campaignId}/pause`,
        {}
      );
    } catch (error) {
      handleError(error, "Failed to pause campaign");
    }
  },

  async resumeCampaign(campaignId: string): Promise<Campaign> {
    try {
      return await apiClient.post<Campaign>(
        `/api/ads/campaigns/${campaignId}/resume`,
        {}
      );
    } catch (error) {
      handleError(error, "Failed to resume campaign");
    }
  },

  async cancelCampaign(campaignId: string): Promise<Campaign> {
    try {
      return await apiClient.post<Campaign>(
        `/api/ads/campaigns/${campaignId}/cancel`,
        {}
      );
    } catch (error) {
      handleError(error, "Failed to cancel campaign");
    }
  },

  async listCampaigns(
    clientId: string,
    statuses?: CampaignStatus[]
  ): Promise<Campaign[]> {
    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);
      if (statuses) {
        statuses.forEach((s) => params.append("status", s));
      }
      const response = await apiClient.get<CampaignListResponse>(
        `/api/ads/campaigns?${params.toString()}`
      );
      return response.campaigns;
    } catch (error) {
      handleError(error, "Failed to list campaigns");
    }
  },

  async getBudget(clientId: string): Promise<BudgetView> {
    try {
      return await apiClient.get<BudgetView>(
        `/api/ads/budget?client_id=${encodeURIComponent(clientId)}`
      );
    } catch (error) {
      handleError(error, "Failed to fetch budget");
    }
  },

  /** Top-level analytics: totals + 30-day spend trend. `window=live`
   *  aggregates active/paused campaigns; `window=all_time` includes
   *  completed and cancelled. */
  async getAnalyticsSummary(
    clientId: string,
    window: "live" | "all_time" = "live"
  ): Promise<AdAnalyticsSummary> {
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        window,
      });
      return await apiClient.get<AdAnalyticsSummary>(
        `/api/ads/analytics/summary?${params.toString()}`
      );
    } catch (error) {
      handleError(error, "Failed to fetch ad analytics");
    }
  },

  /** Month-scoped analytics: each ad_performance row is prorated by its
   *  overlap with the calendar month. Used by /reporting for the
   *  month-over-month ad performance section. */
  async getMonthAnalytics(
    clientId: string,
    year: number,
    month: number,
  ): Promise<AdAnalyticsSummary> {
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        window: "month",
        year: String(year),
        month: String(month),
      });
      return await apiClient.get<AdAnalyticsSummary>(
        `/api/ads/analytics/summary?${params.toString()}`
      );
    } catch (error) {
      handleError(error, "Failed to fetch month ad analytics");
    }
  },

  /** Cheap-version boost ranker. Posts outperforming the client's
   *  engagement-rate baseline, with suggested budget split. No agent. */
  async getBoostCandidates(clientId: string): Promise<BoostCandidatesView> {
    try {
      return await apiClient.get<BoostCandidatesView>(
        `/api/ads/boost-candidates?client_id=${encodeURIComponent(clientId)}`
      );
    } catch (error) {
      handleError(error, "Failed to fetch boost candidates");
    }
  },

  /** Manually import Meta campaigns created outside this system for
   *  the trailing window (30, 60, or 90 days). Returns per-run stats
   *  describing how many were imported, skipped, or unmatched. */
  async importExternalCampaigns(
    clientId: string,
    days: 30 | 60 | 90
  ): Promise<ImportExternalCampaignsResponse> {
    try {
      return await apiClient.post<ImportExternalCampaignsResponse>(
        `/api/ads/import-external`,
        { clientId, days },
        { timeout: 120_000 }
      );
    } catch (error) {
      handleError(error, "Failed to import Meta campaigns");
    }
  },

  /** Per-post performance: organic daily curves + paid overlay +
   * page-level context. Single network round-trip for the modal. */
  async getPostPerformance(
    postId: string,
    windowDays = 30,
  ): Promise<PostPerformanceResponse> {
    try {
      return await apiClient.get<PostPerformanceResponse>(
        `/api/posts/${encodeURIComponent(postId)}/performance?window=${windowDays}`,
      );
    } catch (error) {
      handleError(error, "Failed to load post performance");
    }
  },

  /** Create a standalone-ad campaign for a post.
   *
   * Uploads the creative to Meta and creates the campaign in one call.
   * The creative is expected to be reachable at `creative_file_url` —
   * typically a GCS signed URL for the post's media. Budget is drawn
   * from the same monthly pool as existing-post boosts. */
  async createStandaloneAd(
    request: StandaloneAdRequest,
  ): Promise<StandaloneAdResponse> {
    try {
      return await apiClient.post<StandaloneAdResponse>(
        "/api/ads/standalone-ad",
        request,
        { timeout: AGENT_TIMEOUT_MS },
      );
    } catch (error) {
      handleError(error, "Failed to create standalone ad");
    }
  },

  async setBudget(update: BudgetUpdate): Promise<BudgetView & { id: string }> {
    try {
      return await apiClient.put<BudgetView & { id: string }>(
        "/api/ads/budget",
        update
      );
    } catch (error) {
      handleError(error, "Failed to update budget");
    }
  },
};
